import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/reducer/store";
import { DownloadOutlined, SearchOutlined } from "@ant-design/icons";
import * as echarts from "echarts";
import { Button, Slider, } from "antd";
import {
    AverageTemperatureHistogram,
    IGetLocationsResModel,
    TemperatureStatResponse
} from "@/util/http/ClimateShift/responseType";
import "./index.less";
import {
    getMultipleYearsAverageContours
} from "@/util/http/ClimateShift/index";
import {
    updateCommonErrorMessage,
    updateCommonWarnMessage,
    updateDownloadAllLoading
} from "@/redux/actions/climateShiftAction";

// Props: data from parent (TemperatureStatResponse)
interface Props {
    data: TemperatureStatResponse;
    activeKey?: string;
}

const AverageDataChart = React.forwardRef((props: Props & { activeKey?: string }, ref) => {
    const { data, activeKey } = props;

    const dispatch = useDispatch();

    const selectedLocationData = useSelector((state: RootState) => state.climateShiftReducer.selectedLocationData);
    const selectedLocationDataForAverage = useSelector((state: RootState) => state.climateShiftReducer.selectedLocationDataForAverage);

    const [averageContour1, setAverageContour1] = useState<AverageTemperatureHistogram | null>(null);
    const [averageContour2, setAverageContour2] = useState<AverageTemperatureHistogram | null>(null);
    const [averageContour3, setAverageContour3] = useState<AverageTemperatureHistogram | null>(null);
    // Chart and year state
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstanceRef = useRef<echarts.EChartsType | null>(null);
    // 默认选中最新一年
    const years = data.histogramOfYears.map(y => y.year);
    const defaultYearIdx = years.length > 0 ? years.length - 1 : 0;
    const [yearIdx, setYearIdx] = useState(defaultYearIdx);
    const selectedYear = years[yearIdx];
    const yearData = data.histogramOfYears[yearIdx];
    const averageName1 = `Average ${averageContour1?.startYear}-${averageContour1?.endYear} for ${selectedLocationData?.displayName || ""} (${selectedLocationData?.postalCode || ""})`;
    const averageName2 = `Average ${averageContour2?.startYear}-${averageContour2?.endYear} for ${selectedLocationData?.displayName || ""} (${selectedLocationData?.postalCode || ""})`;
    const averageName3 = `Average ${averageContour3?.startYear}-${averageContour3?.endYear} for ${selectedLocationDataForAverage?.displayName || ""} (${selectedLocationDataForAverage?.postalCode || ""})`;


    useEffect(() => {
        if (!chartRef.current || !yearData) return;
        const chart = echarts.init(chartRef.current);
        chartInstanceRef.current = chart;

        // 颜色插值函数
        function hexToRgb(hex: string) {
            hex = hex.replace("#", "");
            if (hex.length === 3) {
                hex = hex.split("").map(x => x + x).join("");
            }
            const num = parseInt(hex, 16);
            return [
                (num >> 16) & 255,
                (num >> 8) & 255,
                num & 255
            ];
        }

        function rgbToHex(rgb: number[]) {
            return (
                "#" + rgb.map(x => x.toString(16).padStart(2, "0")).join("")
            );
        }

        function interpolateColor(start: string, end: string, t: number) {
            const s = hexToRgb(start);
            const e = hexToRgb(end);
            const rgb = s.map((v, i) => Math.round(v + (e[i] - v) * t));
            return rgbToHex(rgb);
        }

        // Chart title/subtitle for export only
        const chartTitle = `Temperature Distribution for ${selectedLocationData?.displayName} (${selectedLocationData?.postalCode})`;
        const chartSubtitle = `Year ${selectedYear}`;

        const option = {
            title: {
                text: chartTitle,
                subtext: chartSubtitle,
                left: "center",
                top: 20,
                subtextStyle: {
                    fontWeight: "normal",
                    color: "#2F3031"
                },
            },
            legend: {
                data: [
                    ...(
                        averageContour1
                            ? [{
                                name: averageName1,
                                icon: "path://M 0 0 L 4 0 L 4 0.5 L 0 0.5 z",
                                itemStyle: { color: "#000000" }
                            }]
                            : []
                    ),
                    ...(
                        averageContour2
                            ? [{
                                name: averageName2,
                                icon: "path://M 0 0 L 2 0 L 2 0.5 L 0 0.5 Z M 3 0 L 5 0 L 5 0.5 L 3 0.5 ZM 6 0 L 8 0 L 8 0.5 L 6 0.5 Z",
                                itemStyle: { color: "#000000" }
                            }]
                            : []
                    ),
                    ...(
                        averageContour3
                            ? [{
                                name: averageName3,
                                icon: "path://M 0 0 L 4 0 L 4 0.5 L 0 0.5 z",
                                itemStyle: { color: "#777777" }
                            }]
                            : []
                    )
                ],
                // bottom: 10,
                // left: "center",
                // orient: "horizontal",
                // itemGap: 20,
                // textStyle: { fontSize: 14 },
                bottom: 25,
            },
            grid: { left: 60, right: 30, top: 150, bottom: 130 },
            xAxis: {
                name: "Temperature (℃)",
                nameLocation: "middle",
                nameGap: 30,
                min: (() => {
                    let min = data.minTemperature;
                    if (averageContour1) {
                        min = Math.min(min, ...averageContour1.xMids);
                    }
                    if (averageContour2) {
                        min = Math.min(min, ...averageContour2.xMids);
                    }
                    if (averageContour3) {
                        min = Math.min(min, ...averageContour3.xMids);
                    }
                    min = Math.floor(min - 0.5);
                    return min % 5 === 0 ? min : min - (min % 5) - (min < 0 && min % 5 !== 0 ? 5 : 0);
                })(),
                max: (() => {
                    let max = data.maxTemperature;
                    if (averageContour1) {
                        max = Math.max(max, ...averageContour1.xMids);
                    }
                    if (averageContour2) {
                        max = Math.max(max, ...averageContour2.xMids);
                    }
                    if (averageContour3) {
                        max = Math.max(max, ...averageContour3.xMids);
                    }
                    max = Math.ceil(max + 0.5);
                    return max % 5 === 0 ? max : max + (5 - (max % 5));
                })(),
                interval: 5,
                type: "value",
                axisLine: { onZero: true },
                splitLine: { show: false },
            },
            yAxis: {
                name: "Number of Hours",
                nameLocation: "middle",
                nameRotate: 90,
                nameGap: 40,
                min: 0,
                max: (() => {
                    const interval = 200;
                    let maxVal = typeof data?.maxCount === "number" ? data.maxCount : 1000;
                    if (averageContour1) {
                        maxVal = Math.max(maxVal, ...averageContour1.counts);
                    }
                    if (averageContour2) {
                        maxVal = Math.max(maxVal, ...averageContour2.counts);
                    }
                    if (averageContour3) {
                        maxVal = Math.max(maxVal, ...averageContour3.counts);
                    }
                    if (maxVal < interval) {
                        return interval;
                    }
                    if (maxVal < interval) {
                        return interval;
                    }
                    const maxTick = Math.ceil(maxVal / interval) * interval;
                    // 如果实际最大值大于最大刻度，则再多显示一个刻度
                    return maxVal > maxTick ? maxTick + interval : maxTick;
                })(),
                interval: 200,
                type: "value",
                axisLine: { onZero: false },
                splitLine: { show: false },
            },
            series: [
                {
                    name: "Number of Hours",
                    type: "bar",
                    data: yearData.xMids.map((x, i) => {
                        // 2°C以下：蓝色系（#0000ff ~ #00ffff）
                        // 2~15°C：指定颜色
                        // 15~20°C：指定颜色
                        // 20℃以上：红色系（#ff0000 ~ #701F29
                        let color;
                        const tempBarColors2To15 = [
                            "#00ffde", "#00ffb5", "#00ff8c", "#00ff63", "#19ff00", "#42ff00", "#6bff00",
                            "#94ff00", "#bdff00", "#e6ff00", "#fff700", "#ffe200", "#ffce00"
                        ];
                        const tempBarColors15To21 = [
                            "#ffb900", "#ffa500", "#ff9000", "#ff7c00", "#ff6700", "ff3e00"];
                        if (x < 1.5) {
                            // 蓝色系：#0000ff ~ #00ffff
                            const minT = Math.min(...yearData.xMids, 2);
                            const t = (x - minT) / (2 - minT || 1);
                            const clampedT = Math.max(0, Math.min(1, t));
                            color = interpolateColor("#0000ff", "#00ffff", clampedT);
                        } else if (x >= 1.5 && x <= 14.5) {
                            // 2~15°C区间依次指定13个颜色
                            const idx = Math.round(x) - 2;
                            color = tempBarColors2To15[idx] || "#ff3d00";
                        } else if (x > 14.5 && x < 21) {
                            // 15~21°C区间依次指定6个颜色
                            const idx = Math.round(x) - 15;
                            color = tempBarColors15To21[idx] || "#701F29";
                        }
                        else {
                            // 红色系：#ff0000 ~ #B22222（火砖红）
                            const maxT = Math.max(...yearData.xMids, 15);
                            const t = (x - 15) / (maxT - 15 || 1);
                            const clampedT = Math.max(0, Math.min(1, t));
                            color = interpolateColor("#ff0000", "#B22222", clampedT);
                        }
                        return {
                            value: [x, yearData.counts[i]],
                            itemStyle: { color }
                        };
                    }),
                    barWidth: "80%",
                    markLine: {
                        symbol: "none",
                        data: [
                            {
                                xAxis: 1.5,
                                lineStyle: { type: "dashed", color: "#3B82F6" },
                                label: { formatter: "2℃", color: "#3B82F6" }
                            },
                            {
                                xAxis: 14.5,
                                lineStyle: { type: "dashed", color: "#DC2626" },
                                label: { formatter: "15℃", color: "#DC2626" }
                            }
                        ]
                    },
                },
                // data from site location average
                {
                    name: averageName1,
                    type: "line",
                    data: averageContour1?.xMids.map((x, i) => [x, averageContour1?.counts[i]]),
                    smooth: true,
                    symbol: "none", //不显示数据标记点
                    lineStyle: {
                        color: "#000",
                        type: "solid"
                    },
                },
                {
                    name: averageName2,
                    type: "line",
                    data: averageContour2?.xMids.map((x, i) => [x, averageContour2?.counts[i]]),
                    smooth: true,
                    symbol: "none", //不显示数据标记点
                    lineStyle: {
                        color: "#000",
                        type: "dashed"
                    },
                },
                ...(averageContour3 ? [{
                    name: averageName3,
                    type: "line",
                    data: averageContour3?.xMids.map((x, i) => [x, averageContour3?.counts[i]]),
                    smooth: true,
                    symbol: "none", //不显示数据标记点
                    lineStyle: {
                        color: "#777777",
                        type: "solid"
                    },
                }] : [])
            ]
        };
        chart.setOption(option);
        setTimeout(() => {
            chart.resize();
        }, 0);
        return () => chart.dispose();
    }, [yearIdx, data, activeKey, selectedLocationData, selectedLocationDataForAverage, averageContour1, averageContour2, averageContour3]);

    // 下载图表为图片
    const handleDownloadChart = () => {
        if (chartInstanceRef.current) {
            const url = chartInstanceRef.current.getDataURL({
                type: "png",
                pixelRatio: 2,
                backgroundColor: "#fff"
            });
            const link = document.createElement("a");
            link.href = url;
            link.download = `Temperature_Distribution_${selectedLocationData?.postalCode || "chart"}.png`;
            link.click();
        }
    };

    // 暴露给父组件获取指定年份图片
    React.useImperativeHandle(ref, () => ({
        getChartImage: async (targetYearIdx?: number) => {
            // 切换到指定年份
            const idx = typeof targetYearIdx === "number" ? targetYearIdx : yearIdx;
            if (idx < 0 || idx >= years.length) return null;
            setYearIdx(idx);
            // 等待图表渲染
            await new Promise(resolve => setTimeout(resolve, 300));
            const chart = chartInstanceRef.current;
            if (chart) {
                return chart.getDataURL({
                    type: "png",
                    pixelRatio: 2,
                    backgroundColor: "#fff"
                });
            }
            return null;
        }
    }));

    const getAverageContoursData = async (
        ranges: number[],
        locationData: IGetLocationsResModel | null
    ): Promise<AverageTemperatureHistogram[] | null> => {
        if (!locationData || !locationData.lng || !locationData.lat) {
            dispatch(updateCommonErrorMessage("No location selected or missing coordinates."));
            return null;
        }
        try {
            const res = await getMultipleYearsAverageContours({
                longitude: locationData.lng,
                latitude: locationData.lat,
                ranges: ranges,
            });
            if (res.status !== 200) {
                dispatch(updateCommonErrorMessage("Failed to fetch average contours data."));
                return null;
            }
            if (res.data && res.data.length > 0) {
                return res.data;
            } else {
                dispatch(updateCommonWarnMessage("No average contours data found for the selected location."));
                return [];
            }
        } catch (error) {
            dispatch(updateCommonErrorMessage("Error fetching average contours data."));
            console.error(error);
            return null;
        }
    };
    //拿取3个折线图数据
    useEffect(() => {
        // 1. 设置 averageContour1 和 averageContour2
        if (selectedLocationData) {
            // getAverageContoursData([10], selectedLocationData).then(data => {
            //     if (data && data.length > 0) {
            //         setAverageContour1(data[0]);
            //     } else {
            //         setAverageContour1(null);
            //     }
            // });
            // getAverageContoursData([20], selectedLocationData).then(data => {
            //     if (data && data.length > 0) {
            //         setAverageContour2(data[0]);
            //     } else {
            //         setAverageContour2(null);
            //     }
            // });
            getAverageContoursData([10, 20], selectedLocationData).then(data => {
                if (data && data.length > 0) {
                    setAverageContour1(data[0]);
                    setAverageContour2(data[1]);
                } else {
                    setAverageContour1(null);
                    setAverageContour2(null);
                }
            });
        } else {
            setAverageContour1(null);
            setAverageContour2(null);
        }
        // 3. 如果 selectedLocationDataForAverage 存在，优先用它设置 averageContour3
        // 3. 如果 selectedLocationDataForAverage 存在且与 selectedLocationData 不同，优先用它设置 averageContour3
        if (
            selectedLocationDataForAverage &&
            (
                selectedLocationDataForAverage.placeName !== selectedLocationData.placeName ||
                selectedLocationDataForAverage.postalCode !== selectedLocationData.postalCode ||
                selectedLocationDataForAverage.displayName !== selectedLocationData.displayName
            )
        ) {
            getAverageContoursData([10], selectedLocationDataForAverage).then(data => {
                if (data && data.length > 0) {
                    setAverageContour3(data[0]);
                } else {
                    setAverageContour3(null);
                }
            });
        } else {
            setAverageContour3(null);
        }
    }, [selectedLocationDataForAverage, selectedLocationData]);

    return (
        <div className="average-data-chart-container">
            <div className="average-data-chart-top-area">
                <div className="download-img" >
                    <Button className="download-btn download-btn-icon-right"
                        onClick={handleDownloadChart}>
                        <span className="download-btn-icon"><DownloadOutlined /></span>
                    </Button>
                </div>
                <div className="slider-div">
                    <span style={{ marginRight: 8, whiteSpace: "nowrap", fontSize: "14px" }}>Year:</span>
                    <Slider
                        min={0}
                        max={years.length - 1}
                        value={yearIdx}
                        onChange={setYearIdx}
                        step={1}
                        style={{ flex: 1, minWidth: 120, maxWidth: 300 }}
                    />
                    <span style={{ marginLeft: 8, whiteSpace: "nowrap", fontSize: "14px" }}>{selectedYear}</span>
                </div>
                <div ref={chartRef} style={{ width: "100%", minWidth: "900px", height: 490 }} />
            </div>
        </div>
    );
});

AverageDataChart.displayName = "AverageDataChart";
export default AverageDataChart;
